import { useState, useEffect, useRef, useMemo } from 'react';
import {
	LocationPin,
	LocationWhite,
	Map,
	CurrentLocationMarker
} from '@/resources/assets/images';
import RadiusSlider from '@/components/RadiusSlider/RadiusSlider';
import './SearchTabLocation.scss';
import type { RootState } from '@/rootReducer';
import { useSelector } from 'react-redux';
import {
	BusinessQuery,
	FilterQueryType
} from '@/modules/dashboard/dashboardTypes';
import { sendAnalytics } from '@/services/analyticsService';
import { fromJS } from 'immutable';
import {
	stateMapping,
	countryMapping
} from '@/components/HeaderRefactored/stateCountryMapping';
import { useMapboxKeyQuery } from '@/hooks/useMapBoxKeyQuery';
import { cn } from '@/lib/classNames';
import { isQueryDifferent } from '../src/helpers/isQueryDifferent';
import { useSearchTabContext } from '../SearchTabContext';
import SearchTabLocationSearchBar from './SearchTabLocationSearchBar/SearchTabLocationSearchBar';
import SearchTabFiltersListItemHeader from '../SearchTabFiltersListItemHeader';
import { reverseGeocode } from '../src/helpers/reverseGeocode';

interface SearchTabLocationProps {
	handleSwitchView?: (view: 'businesses' | 'map') => void;
	onFilterChange: (query: BusinessQuery) => void;
	onGetBusiness: (query: BusinessQuery) => void;
}
const SearchTabLocation = ({
	handleSwitchView,
	onFilterChange,
	onGetBusiness
}: SearchTabLocationProps) => {
	const [query, user] = useSelector((state: RootState) => [
		state.dashboard.get('query').toJS(),
		state.user.getIn(['authorize', 'user']).toJS()
	]);
	const { data: mapboxKeyData } = useMapboxKeyQuery();
	const mapboxAPIKey = mapboxKeyData?.data?.data?.mapBoxAPIKey;
	const { selectedListItem, selectedLocation, setSelectedLocation } =
		useSearchTabContext();
	const [tabExpanded, setTabExpanded] = useState(false);
	const previousStateRef = useRef({
		coordinates: query.coordinates,
		locationName: selectedLocation
	});
	const [isSliderDisabled, setIsSliderDisabled] = useState(false);
	const [showMapButtonVisible, setShowMapButtonVisible] = useState(false);
	const [resolvedLocation, setResolvedLocation] = useState<string | null>(''); // State for resolved location name

	const resetLocationFilter = () => {
		const newQuery = { ...query };

		delete newQuery.coordinates;
		delete newQuery.state;
		delete newQuery.country;

		// Reset max and min values
		newQuery.maxDistance = 40;
		newQuery.minDistance = 0;
		newQuery.selectedLocation = undefined;

		// Reset sort to default based on user's ab test group
		if (!newQuery.listId)
			newQuery.sort = { score: -1 };
		else {
			delete newQuery.sort;
		}

		sendAnalytics({
			type: 'track',
			userId: user.email,
			properties: {
				selectedLocation
			},
			eventName: 'User cleared location filter'
		});

		onFilterChange(newQuery);

		const queryChanged = isQueryDifferent(newQuery);

		if (queryChanged) {
			onGetBusiness(newQuery);
		}

		setResolvedLocation('');

		setSelectedLocation('');
		handleSwitchView?.('businesses');
	};

	const effectiveLocation = useMemo(() => {
		return selectedLocation || resolvedLocation || undefined;
	}, [resolvedLocation, selectedLocation]);

	useEffect(() => {
		// Update previous state
		previousStateRef.current = {
			coordinates: query.coordinates,
			locationName: selectedLocation
		};

		if (effectiveLocation) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				properties: {
					selectedLocation: effectiveLocation
				},
				eventName: 'User added a new location successfully location tab'
			});
		}
	}, [effectiveLocation, selectedLocation, user.email]);

	useEffect(() => {
		if (!selectedLocation) return;
		let isState = false;
		let isCountry = false;
		const newQuery = { ...query };

		if (!query.selectedLocation && !selectedLocation) return;
		const parts =
			query.selectedLocation ||
			selectedLocation.split(',').map(part => part.trim());

		if (parts.length === 2) {
			const [stateInput, countryInput] = parts;

			const validState =
				stateMapping[stateInput] ||
				(Object.values(stateMapping).includes(stateInput)
					? stateInput
					: null);

			const validCountry =
				countryMapping[countryInput] ||
				(Object.values(countryMapping).includes(countryInput)
					? countryInput
					: null);

			if (validState) {
				newQuery.state = validState;
				isState = true;
			}

			if (validCountry) {
				newQuery.country = validCountry;
				isCountry = true;
			}
		} else {
			const validCountry =
				countryMapping[parts] ||
				(Object.values(countryMapping).includes(parts) ? parts : null);

			if (validCountry) {
				newQuery.country = validCountry;
				isCountry = true;
			}
		}

		// Only update query if changes exist
		if (
			newQuery.state !== query.state ||
			newQuery.country !== query.country
		) {
			onFilterChange(newQuery);
			onGetBusiness(newQuery);
		}

		// Disable slider if state or country is selected
		setIsSliderDisabled(isState || isCountry);
	}, [selectedLocation, stateMapping, countryMapping]);

	useEffect(() => {
		// Trigger the animation when coordinates are present
		if (query.coordinates) {
			setShowMapButtonVisible(true);
		}
	}, [query.coordinates]);

	useEffect(() => {
		// Trigger the animation when coordinates are present
		if (query.coordinates) {
			setShowMapButtonVisible(true);
		} else {
			setShowMapButtonVisible(false);
		}
	}, [query.coordinates]);

	useEffect(() => {
		// Expand the tab when coordinates are present
		setTabExpanded(!!query.coordinates);
	}, [query.coordinates]);

	// Local state for managing the radius slider
	const [radius, setRadius] = useState({
		min: query.minDistance || 0,
		max: query.maxDistance || 40
	});

	useEffect(() => {
		setRadius({
			min: query.minDistance || 0,
			max: query.maxDistance > 40 ? 40 : query.maxDistance || 0
		});
	}, [query.minDistance, query.maxDistance]);

	// Modify trimmedLocation to ensure it reflects the change
	const trimmedLocation = async (location: string) => {
		const isNonDescriptiveName = /^[a-f0-9]{24}$/.test(location);

		if ((isNonDescriptiveName || location === '') && query.coordinates) {
			const returnVal = await reverseGeocode({
				coordinatesInput: query.coordinates,
				accessToken: mapboxAPIKey
			});
			return returnVal || '';
		}

		const lastCommaIndex = location.lastIndexOf(',');
		return lastCommaIndex > -1
			? location.substring(0, lastCommaIndex)
			: location;
	};

	const radiusOnChange = (val: Range | number) => {
		const queryNew = fromJS(query).toJS();

		queryNew.minDistance = (val as Range).min;
		queryNew.maxDistance = (val as Range).max;

		if (!queryNew.count) {
			queryNew.count = true;
		}

		queryNew.limit = 50;

		queryNew.skip = 0;

		queryNew.queryType = FilterQueryType.FILTER;

		onFilterChange(queryNew);
		onGetBusiness(queryNew);

		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'User changed filters',
				properties: {
					filterChanged: 'Radius',
					query: queryNew
				}
			});
		}
	};

	useEffect(() => {
		const newQuery = { ...query };

		if (
			previousStateRef.current.locationName === '' &&
			selectedLocation === ''
		) {
			setResolvedLocation('');
		}
		if (
			!query.selectedLocation &&
			!selectedLocation &&
			(query.coordinates || query.state || query.country)
		) {
			delete newQuery.state;
			delete newQuery.country;
			const fetchResolvedLocation = async () => {
				const resolved = await trimmedLocation('');
				setResolvedLocation(resolved);
				newQuery.selectedLocation = resolved;
				onFilterChange(newQuery);
				onGetBusiness(newQuery);
			};

			fetchResolvedLocation();
		}
	}, [selectedLocation]);

	const isLocationActive = Boolean(
		effectiveLocation &&
			(query.coordinates ||
				(query.country && query.state) ||
				query.country)
	);

	return (
		<div
			className={`search-tab-filters-list-item ${selectedListItem === 'Location' ? 'opened' : ''} ${isLocationActive ? 'filter-selected' : ''} ${tabExpanded ? 'tab-expanded' : ''} `}
		>
			<SearchTabFiltersListItemHeader
				filterName="Location"
				filterCount={1}
				onClick={resetLocationFilter}
				displayClearFiltersButton={isLocationActive}
				icon={
					<LocationPin className="search-tab-filter-list-item-icon location" />
				}
			/>

			<div className="search-tab-filters-list-item-dropdown">
				<div className="search-tab-filters-list-item-dropdown-divider" />

				{isLocationActive && (
					<span
						className={`search-tab-filters-list-item-dropdown-location-chip ${selectedLocation === 'Current location' ? 'current-location-chip' : ''} `}
					>

						{selectedLocation === 'Current location' ? (
							<div className="align-center flex justify-center gap-2">
								<CurrentLocationMarker className="search-tab-filters-list-item-dropdown-location-chip-icon-current-location" />
								<span className="search-tab-filters-list-item-dropdown-location-chip-text">
									Current location
								</span>
							</div>
						) : (
							<div className="flex">
								<LocationWhite className="search-tab-filters-list-item-dropdown-location-chip-icon" />
								<span className="search-tab-filters-list-item-dropdown-location-chip-text">
									{effectiveLocation}
								</span>
							</div>
						)}
					</span>
				)}

				<div>Search and select a new location</div>
				<SearchTabLocationSearchBar
					setSelectedLocation={setSelectedLocation}
					onFilterChange={onFilterChange}
					onGetBusiness={onGetBusiness}
				/>
				<div
					data-disabled={isSliderDisabled}
					className="search-tab-location-radius-section"
				>
					{(query.state || query.country) && (
						<div className="radius-tooltip">
							Radius slider and map button are disabled when a
							state/country is chosen
						</div>
					)}
					<div
						data-disabled={isSliderDisabled || !query.coordinates}
						className="search-tab-filters-list-item-dropdown-location-slider-header"
					>
						Radius (in miles)
					</div>
					<div className="search-tab-filters-list-item-dropdown-location-slider-container">
						<RadiusSlider
							className="search-tab-filters-list-item-dropdown-location-slider"
							minRadius={0}
							maxRadius={40}
							current={radius}
							onChangeComplete={radiusOnChange}
							disabled={isSliderDisabled || !query.coordinates} // Disable the slider
						/>
					</div>
					{query.coordinates && (
						<div
							data-is-visible={showMapButtonVisible}
							className={cn(
								'mt-3 hidden justify-center lg:flex',
								'data-[is-visible=true]:fade-in',
								'data-[is-visible=false]:fade-out'
							)}
						>
							<button
								type="button"
								data-disabled={
									isSliderDisabled ||
									!query.coordinates ||
									query.state ||
									query.country
								}
								className="search-tab-filters-list-item-dropdown-location-show-map-button"
								onClick={() => {
									sendAnalytics({
										type: 'track',
										userId: user.email,
										properties: {
											selectedLocation
										},
										eventName:
											'User pressed show on map in location tab'
									});
									handleSwitchView?.('map');
								}}
								disabled={
									isSliderDisabled ||
									!query.coordinates ||
									query.state ||
									query.country
								}
							>
								Show on map
								<Map />
							</button>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default SearchTabLocation;
